<!-- 新闻详情页面 -->
<template>
  <div class="news-detail-page">
    <Navbar />

    <!-- 页头 -->
    <section class="detail-hero">
      <div class="detail-hero-overlay"></div>
      <div class="detail-hero-particles">
        <span v-for="n in 8" :key="n" class="particle" :style="{ '--i': n }"></span>
      </div>
      <div class="container">
        <div class="detail-hero-content">
          <span class="section-tag">LATEST NEWS</span>
          <h1 class="detail-hero-title">新闻资讯</h1>
          <p class="detail-hero-desc">行业动态 · 品牌资讯 · 技术分享</p>
          <div class="hero-divider">
            <span class="line"></span>
            <span class="diamond"></span>
            <span class="line"></span>
          </div>
        </div>
      </div>
    </section>

    <!-- 新闻主体 -->
    <section class="detail-content">
      <div class="container">
        <div class="detail-layout">
          <!-- 左侧：正文 -->
          <main class="detail-main">
            <article class="detail-article">
              <div class="article-top">
                <h1 class="article-title">{{ currentNews.title }}</h1>
                <div class="article-meta">
                  <span class="meta-item">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
                    {{ whichTime(currentNews.editTime) }}
                  </span>
                  <span class="meta-dot">·</span>
                  <span class="meta-item">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
                    科曼驰新闻资讯
                  </span>
                  <span class="meta-dot">·</span>
                  <span class="meta-tag">{{ getCategoryLabel(currentNews.category) }}</span>
                  <span class="meta-dot">·</span>
                  <span class="meta-item meta-views">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
                    {{ currentNews.viewCount || 0 }} 阅读
                  </span>
                </div>
              </div>

              <div class="article-divider">
                <span class="line"></span>
                <span class="diamond"></span>
                <span class="line"></span>
              </div>

              <div class="article-body" v-html="currentNews.content"></div>
            </article>

            <!-- 返回 -->
            <div class="article-back">
              <router-link to="/news" class="back-btn">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
                返回新闻列表
              </router-link>
            </div>
          </main>

          <!-- 右侧：侧边栏 -->
          <aside class="detail-sidebar">
            <div class="sidebar-card">
              <div class="sidebar-title">
                <span>最近新闻</span>
                <span class="sidebar-badge">LATEST</span>
              </div>
              <div class="sidebar-list">
                <div
                  v-for="(item, i) in topNews"
                  :key="item._id"
                  class="sidebar-item"
                  :class="{ active: item._id === currentNews._id }"
                  @click="goDetail(item._id)"
                >
                  <span class="sidebar-num" :class="{ top3: i < 3 }">{{ String(i + 1).padStart(2, '0') }}</span>
                  <div class="sidebar-item-text">
                    <span class="sidebar-item-title">{{ item.title }}</span>
                    <span class="sidebar-item-time">{{ whichTime(item.editTime) }}</span>
                  </div>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="9 18 15 12 9 6"/></svg>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </div>
    </section>

    <el-backtop :visibility-height="100" />
  </div>
</template>

<script setup>
import { ref, watchEffect, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import axios from 'axios'
import moment from 'moment'
import Navbar from '@/components/Navbar.vue'

moment.locale('zh-cn')
const route = useRoute()
const router = useRouter()

const currentNews = ref({})
const topNews = ref([])
const mobileMenuOpen = ref(false)

const stop = watchEffect(async () => {
  if (!route.params.id) return
  try {
    const [res1, res2] = await Promise.all([
      axios.get(`/webapi/news/list/${route.params.id}`),
      axios.get('/webapi/news/toplist?limit=6')
    ])
    currentNews.value = res1.data.data?.[0] || {}
    topNews.value = res2.data.data || []
    // 增加浏览次数
    axios.post(`/webapi/news/view/${route.params.id}`)
  } catch (e) {
    console.error('获取新闻详情失败:', e)
  }
})

onBeforeUnmount(() => stop())

const whichTime = (time) => {
  if (!time) return ''
  return moment(time).format('YYYY年MM月DD日')
}

const getCategoryLabel = (cat) => {
  const map = { 1: '最新动态', 2: '产品预告', 3: '通知公告' }
  return map[cat] || '新闻'
}

const goDetail = (id) => {
  router.push(`/news/${id}`)
  window.scrollTo({ top: 0, behavior: 'smooth' })
}
</script>

<style scoped lang="scss">
// ===== 变量 =====
$gold: #d4af37;
$dark: #1a1a1a;
$gray: #6b7280;
$light-gray: #f8f9fa;
$border: #e5e7eb;

// ===== 全局 =====
.container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

// ===== 页头 =====
.detail-hero {
  position: relative;
  min-height: 520px;
  background: linear-gradient(135deg, $dark 0%, #2a2a2a 100%);
  display: flex;
  align-items: center;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -20%;
    right: -5%;
    width: 700px;
    height: 700px;
    background: radial-gradient(circle, rgba(212,175,55,0.08) 0%, transparent 70%);
    border-radius: 50%;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, $gold, transparent);
    opacity: 0.3;
  }
}

.detail-hero-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(135deg, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.2) 100%);
}

.detail-hero-particles {
  position: absolute;
  inset: 0;
  pointer-events: none;

  .particle {
    position: absolute;
    width: 4px;
    height: 4px;
    background: $gold;
    border-radius: 50%;
    opacity: 0;
    box-shadow: 0 0 8px $gold;
    animation: floatParticle 4s ease-in-out infinite;
    animation-delay: calc(var(--i) * 0.4s);
  }
}

@keyframes floatParticle {
  0%, 100% { transform: translateY(0) scale(1); opacity: 0.2; }
  50% { transform: translateY(-40px) scale(1.3); opacity: 0.6; }
}

.detail-hero-content {
  position: relative;
  z-index: 2;
  padding: 60px 0;
  text-align: center;

  .section-tag {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 4px;
    text-transform: uppercase;
    color: $gold;
    display: block;
    margin-bottom: 16px;
  }

  .detail-hero-title {
    font-size: 52px;
    font-weight: 800;
    color: #fff;
    letter-spacing: 2px;
    margin-bottom: 16px;
  }

  .detail-hero-desc {
    font-size: 17px;
    color: rgba(255,255,255,0.6);
    letter-spacing: 1px;
  }
}

.hero-divider {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  margin: 28px 0;

  .line {
    width: 80px;
    height: 1px;
    background: linear-gradient(to right, transparent, $gold, transparent);
    opacity: 0.5;
  }

  .diamond {
    width: 6px;
    height: 6px;
    background: $gold;
    transform: rotate(45deg);
  }
}

// ===== 内容区 =====
.detail-content {
  padding: 48px 0 80px;
  background: $light-gray;
  min-height: 60vh;
}

.detail-layout {
  display: grid;
  grid-template-columns: 1fr 320px;
  gap: 32px;
  align-items: start;
}

// ===== 正文 =====
.detail-main {
  .detail-article {
    background: #fff;
    border-radius: 16px;
    padding: 48px 56px;
    box-shadow: 0 2px 16px rgba(0,0,0,0.04);
  }

  .article-top {
    margin-bottom: 28px;
  }

  .article-title {
    font-size: 30px;
    font-weight: 800;
    color: $dark;
    line-height: 1.35;
    margin-bottom: 16px;
    letter-spacing: -0.5px;
  }

  .article-meta {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: $gray;

    .meta-item {
      display: flex;
      align-items: center;
      gap: 5px;
    }

    .meta-dot { color: $border; }

    .meta-tag {
      padding: 2px 10px;
      font-size: 12px;
      font-weight: 500;
      color: $gold;
      background: rgba(212,175,55,0.1);
      border: 1px solid rgba(212,175,55,0.2);
      border-radius: 20px;
    }

    .meta-views {
      color: $gray;
    }
  }

  .article-divider {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 24px 0 32px;

    .line {
      flex: 1;
      height: 1px;
      background: linear-gradient(to right, transparent, $border, transparent);
    }

    .diamond {
      width: 6px;
      height: 6px;
      background: $gold;
      transform: rotate(45deg);
      flex-shrink: 0;
    }
  }

  .article-body {
    font-size: 16px;
    line-height: 1.9;
    color: #333;

    :deep(p) { margin-bottom: 16px; }
    :deep(img) {
      max-width: 100%;
      height: auto;
      border-radius: 8px;
      margin: 20px 0;
      box-shadow: 0 4px 16px rgba(0,0,0,0.06);
    }
    :deep(h2), :deep(h3) {
      margin: 28px 0 12px;
      color: $dark;
      font-weight: 700;
    }
    :deep(ul), :deep(ol) {
      padding-left: 24px;
      margin-bottom: 16px;
    }
    :deep(blockquote) {
      padding: 16px 24px;
      border-left: 4px solid $gold;
      background: $light-gray;
      border-radius: 0 8px 8px 0;
      margin: 16px 0;
      font-style: italic;
      color: $gray;

      p { margin-bottom: 0; }
    }
  }
}

.article-back {
  margin-top: 24px;

  .back-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 24px;
    font-size: 14px;
    font-weight: 500;
    color: $gray;
    text-decoration: none;
    border: 1px solid $border;
    border-radius: 8px;
    transition: all 0.3s ease;

    &:hover {
      color: $gold;
      border-color: $gold;
      background: rgba(212,175,55,0.04);
    }
  }
}

// ===== 侧边栏 =====
.detail-sidebar {
  .sidebar-card {
    background: #fff;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 2px 16px rgba(0,0,0,0.04);
    position: sticky;
    top: 104px;

    .sidebar-title {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: 16px;
      border-bottom: 1px solid $border;
      margin-bottom: 4px;

      span {
        font-size: 15px;
        font-weight: 700;
        color: $dark;
      }

      .sidebar-badge {
        font-size: 10px;
        font-weight: 600;
        letter-spacing: 1px;
        color: $gold;
        background: rgba(212,175,55,0.1);
        padding: 3px 10px;
        border-radius: 10px;
      }
    }

    .sidebar-list {
      .sidebar-item {
        display: flex;
        align-items: flex-start;
        gap: 12px;
        padding: 14px 4px;
        border-bottom: 1px solid $light-gray;
        cursor: pointer;
        transition: all 0.3s ease;
        border-radius: 6px;

        &:last-child { border-bottom: none; }

        &:hover {
          background: rgba(212,175,55,0.04);

          .sidebar-item-title { color: $gold; }
          .sidebar-arrow { opacity: 1; transform: translateX(4px); }
        }

        &.active {
          background: rgba(212,175,55,0.06);

          .sidebar-num { background: $gold; color: #fff; }
          .sidebar-item-title { color: $gold; }
        }

        .sidebar-num {
          flex-shrink: 0;
          width: 26px;
          height: 26px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
          font-weight: 700;
          color: $gray;
          background: $light-gray;
          border-radius: 6px;
          margin-top: 2px;
          transition: all 0.3s ease;

          &.top3 {
            color: $gold;
            background: rgba(212,175,55,0.1);
          }
        }

        .sidebar-item-text {
          flex: 1;
          min-width: 0;

          .sidebar-item-title {
            font-size: 14px;
            font-weight: 500;
            color: $dark;
            line-height: 1.45;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            transition: color 0.3s ease;
          }

          .sidebar-item-time {
            display: block;
            font-size: 12px;
            color: #bbb;
            margin-top: 4px;
          }
        }

        .sidebar-arrow {
          flex-shrink: 0;
          color: $gray;
          opacity: 0;
          margin-top: 4px;
          transition: all 0.3s ease;
        }
      }
    }
  }
}

// ===== 响应式 =====
@media (max-width: 1024px) {
  .detail-hero { min-height: 300px; }
  .detail-layout { grid-template-columns: 1fr; }
  .detail-sidebar { display: none; }
  .detail-article { padding: 32px 24px; }
  .article-title { font-size: 24px; }
}

@media (max-width: 768px) {
  .detail-hero { min-height: 340px; }
  .detail-hero-content { padding: 40px 0; }
  .detail-hero-content .detail-hero-title { font-size: 32px; }
  .detail-content { padding: 32px 0 60px; }
  .detail-article { padding: 24px 20px; }
  .article-title { font-size: 20px; }
  .article-body { font-size: 15px; }
}

</style>
